import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Lock, Search } from "lucide-react";
import { localizedInputValidationProps } from "../lib/localizedValidation.js";
import {
  ALL_COUNTRIES,
  type Country,
  digitsOnly,
  formatPhoneLocal,
  getCountryByIso,
  isoToFlag,
  maxLocalDigits,
  parsePhone,
  toE164,
} from "../../../shared/phone.js";

/**
 * <PhoneInput value={...} onChange={...} />
 *
 * Stripe-style international phone input:
 *   - flag + dial-code button on the left, click to open the country picker
 *   - searchable, scrollable list (curated to LatAm / PT / ES / US / EU first)
 *   - the input on the right takes the LOCAL part only
 *   - `onChange` receives the full E.164 string (e.g. "+5511999999999"),
 *     so the parent doesn't need to know about countries at all
 *   - if `value` arrives as E.164 (URL prefill, DB fetch, etc.), we
 *     auto-detect the country and split correctly
 *
 * Accessibility: the trigger is a real <button>, the popover traps focus
 * back to the input on close, and Escape closes the popover.
 */
export function PhoneInput({
  value,
  onChange,
  defaultCountryIso = "BR",
  placeholder,
  autoComplete = "tel",
  required,
  id,
  className,
  disabled = false,
}: {
  value: string;
  onChange: (e164: string) => void;
  defaultCountryIso?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  id?: string;
  className?: string;
  /** Locks both the country picker and the number input (read-only). */
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  // Initial country comes from either the existing value (if E.164) or the
  // explicit default. We keep `country` in component-local state so the user
  // can switch it without the parent caring.
  const initial = useMemo(() => parsePhone(value, defaultCountryIso), []);
  const [country, setCountry] = useState<Country>(initial.country);
  const [local, setLocal] = useState<string>(initial.local);
  const [open, setOpen] = useState(false);

  // Keep our local mirror in sync with the parent if the parent forcibly
  // resets the value (e.g. clears the form). Don't fight typing in either
  // direction otherwise.
  useEffect(() => {
    const parsed = parsePhone(value, defaultCountryIso);
    if (toE164(country, local) !== toE164(parsed.country, parsed.local)) {
      setCountry(parsed.country);
      setLocal(parsed.local);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function emit(nextCountry: Country, nextLocal: string) {
    setCountry(nextCountry);
    setLocal(nextLocal);
    onChange(toE164(nextCountry, nextLocal));
  }

  return (
    <div
      className={`relative flex items-stretch gap-2 ${disabled ? "opacity-70" : ""} ${className ?? ""}`}
    >
      <CountryButton
        country={country}
        open={open}
        disabled={disabled}
        onToggle={() => setOpen((v) => !v)}
      />
      <div className="relative flex-1">
        <input
          id={id}
          className={`input-clean w-full ${disabled ? "pr-9 bg-black/[0.04] cursor-not-allowed" : ""}`}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          required={required}
          disabled={disabled}
          readOnly={disabled}
          aria-disabled={disabled || undefined}
          {...(required ? localizedInputValidationProps(t) : {})}
          placeholder={placeholder ?? country.example ?? ""}
          value={formatPhoneLocal(country, local)}
          onChange={(e) => emit(country, digitsOnly(e.target.value).slice(0, maxLocalDigits(country)))}
        />
        {disabled && (
          <Lock
            className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none"
            aria-hidden="true"
          />
        )}
      </div>
      {open && !disabled && (
        <CountryPopover
          selectedIso={country.iso}
          onPick={(c) => {
            setOpen(false);
            emit(c, local);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function CountryButton({
  country,
  open,
  disabled = false,
  onToggle,
}: {
  country: Country;
  open: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      className={`input-clean inline-flex items-center gap-1.5 px-3 whitespace-nowrap ${disabled ? "cursor-not-allowed bg-black/[0.04]" : "hover:bg-black/[0.02]"}`}
      style={{ width: "auto", flex: "0 0 auto" }}
    >
      <span className="text-lg leading-none" aria-hidden="true">
        {isoToFlag(country.iso)}
      </span>
      <span className="text-sm tabular-nums">{country.dial}</span>
      <ChevronDown className="w-3.5 h-3.5 opacity-60" />
    </button>
  );
}

function CountryPopover({
  selectedIso,
  onPick,
  onClose,
}: {
  selectedIso: string;
  onPick: (c: Country) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside + Escape to dismiss.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_COUNTRIES;
    return ALL_COUNTRIES.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.iso.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <div
      ref={containerRef}
      role="listbox"
      className="absolute left-0 top-full mt-1 z-50 w-72 bg-white rounded-xl shadow-xl border border-black/10 overflow-hidden"
    >
      <div className="p-2 border-b border-black/5">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            autoFocus
            className="input-clean pl-8 text-sm"
            style={{ padding: "8px 10px 8px 28px" }}
            placeholder="Buscar país…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>
      <ul className="max-h-72 overflow-y-auto py-1">
        {filtered.map((c) => (
          <li key={c.iso}>
            <button
              type="button"
              role="option"
              aria-selected={c.iso === selectedIso}
              onClick={() => onPick(c)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-black/[0.04] text-left"
              style={c.iso === selectedIso ? { background: "rgba(124, 92, 255, 0.08)" } : undefined}
            >
              <span className="text-base leading-none w-5 text-center" aria-hidden="true">
                {isoToFlag(c.iso)}
              </span>
              <span className="flex-1">{c.label}</span>
              <span className="text-ink-2 tabular-nums">{c.dial}</span>
            </button>
          </li>
        ))}
        {filtered.length === 0 && (
          <li className="px-3 py-4 text-sm text-ink-2 text-center">—</li>
        )}
      </ul>
    </div>
  );
}

export { getCountryByIso };
